import React from 'react';
import { useI18n } from '@shopify/react-i18n';
import enTranslations from './translations/en.json';
import frTranslations from './translations/fr.json';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

/**
 * OAuthRequired
 *
 * Shown transiently when a store has no valid access token (first install, or after uninstall).
 * Auto-redirects via window.shopify.redirectToOAuth (App Bridge CDN v4) when available.
 * Falls back to a visible button requiring a user click — the only browser-safe way to
 * break out of a cross-origin iframe without App Bridge assistance.
 *
 * NOTE: Programmatic clicks on target="_top" anchors are NOT treated as user gestures by
 * browsers in cross-origin iframes and will cause "accounts.shopify.com refused to connect".
 * Do NOT attempt to auto-click the fallback button.
 *
 * The server injects two globals into index.html before serving it:
 *   window.__MDF_NEEDS_OAUTH__ = true
 *   window.__MDF_OAUTH_PATH__  = '/auth/shopify?shop=...'
 */
export default function OAuthRequired() {
  const [i18n] = useI18n({
    id: 'OAuthRequired',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  const oauthPath = window.__MDF_OAUTH_PATH__ || '/auth/shopify';

  // Auto-redirect is not possible from a cross-origin iframe without a user gesture.
  // App Bridge CDN v4 does not expose redirectToOAuth, and window.top.location.href
  // is blocked by Chrome. The anchor below (target="_top" + real click) is the only
  // reliable way to break out of the iframe.

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: '20px',
      padding: '32px 24px',
      textAlign: 'center',
      boxSizing: 'border-box',
    }}>
      <s-badge icon="lock" style={{ fontSize: '16px', padding: '12px 20px' }}>
        {i18n.translate('OAuthRequired.title')}
      </s-badge>

      <s-text tone="subdued" style={{ maxWidth: '420px' }}>
        {i18n.translate('OAuthRequired.description')}
      </s-text>

      {/*
        Real user click on target="_top" is the only reliable cross-origin iframe escape
        when App Bridge's redirectToOAuth is not available. Do NOT trigger this
        programmatically — browsers require a genuine user gesture for target="_top".
      */}
      <a href={oauthPath} target="_top" style={{ textDecoration: 'none' }}>
        <s-button variant="primary">
          {i18n.translate('OAuthRequired.authorizeButton')}
        </s-button>
      </a>
    </div>
  );
}
